import { MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { headingVariants, textVariants } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

type PageHeaderActionItem = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "default" | "destructive";
  seperator?: false;
};

type PageHeaderActionSeperator = {
  label?: string;
  seperator: boolean;
};

type PageHeaderAction = PageHeaderActionItem | PageHeaderActionSeperator;

interface PageHeaderProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  variant?: "default" | "compact";
  visual?: React.ReactNode;
  backTo?: string;
  actions?: PageHeaderAction[];
  tags?: React.ReactNode;
  toolbar?: React.ReactNode;
  containerClassName?: string;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  variant = "default",
  visual,
  actions = [],
  tags,
  toolbar,
  containerClassName,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 pb-2 md:flex-row md:items-start md:justify-between md:pb-3",
        variant === "compact" && "gap-3 pb-1 md:pb-2",
        containerClassName
      )}
    >
      <div className="min-w-0">
        <div className="space-y-2 pr-4">
          {eyebrow ? (
            <p className={textVariants({ variant: "eyebrow" })}>{eyebrow}</p>
          ) : null}
          {title && (
            <h1
              className={headingVariants({ variant: "page" })}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Only internal data
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
          {description && (
            <p
              className={cn(
                "max-w-3xl",
                textVariants({ variant: "body", tone: "muted" })
              )}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Only internal data
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          {tags ? (
            <div
              className="mt-2 flex flex-wrap gap-2 items-center"
              title="page tags"
            >
              {tags}
            </div>
          ) : null}
        </div>
      </div>
      {toolbar || actions.length > 0 ? (
        <div className="flex shrink-0 items-center gap-2">
          {toolbar}
          {actions.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="light" size="icon" aria-label="Actions">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {actions.map((action, idx) => {
                  if (action.seperator) {
                    return (
                      <DropdownMenuSeparator
                        key={action.label ?? `separator-${idx}`}
                      />
                    );
                  }

                  const itemAction = action as PageHeaderActionItem;
                  return (
                    <DropdownMenuItem
                      key={itemAction.label}
                      onClick={itemAction.onClick}
                      disabled={itemAction.disabled}
                      variant={itemAction.variant}
                    >
                      {itemAction.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}
      {visual ? <div className="shrink-0">{visual}</div> : null}
    </div>
  );
}
